import { lazy, Suspense, useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { BootSkeleton, Button, ErrorBoundary, IconMoon, IconSun, LoadingSkeleton } from './components'
import { queryClient, useStats } from './api'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { LoginPage, LoginPageSkeleton } from './features/auth/LoginPage'
import { DEFAULT_PATH, TABS, isTabId, pathForTab, tabForPath } from './routes'
import { AppShell, type NavItem } from './shell'
import { uiStore, useUiStore } from './store'
import { applyTheme } from './theme/theme'
import css from './App.module.css'

// Route pages load per tab. Each dynamic import targets one tab-<id> module so
// Rollup emits exactly one chunk per tab; the webpackChunkName comments record
// the intended grouping for bundlers that honour them.
const AssetsPage = lazy(() => import(/* webpackChunkName: "tab-assets" */ './routes/chunks/tab-assets').then((m) => ({ default: m.AssetsPage })))
const EntriesTab = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.EntriesTab })))
const DailyReview = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.DailyReview })))
const DailySummary = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.DailySummary })))
const InboxTriage = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.InboxTriage })))
const SemanticSearch = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.SemanticSearch })))
const V3ResourcesTab = lazy(() => import(/* webpackChunkName: "tab-knowledge" */ './routes/chunks/tab-knowledge').then((m) => ({ default: m.V3ResourcesTab })))
const InboxTab = lazy(() => import(/* webpackChunkName: "tab-inbox" */ './routes/chunks/tab-inbox').then((m) => ({ default: m.InboxTab })))
const MessagesTab = lazy(() => import(/* webpackChunkName: "tab-messages" */ './routes/chunks/tab-messages').then((m) => ({ default: m.MessagesTab })))
const HardwarePage = lazy(() => import(/* webpackChunkName: "tab-hardware" */ './routes/chunks/tab-hardware').then((m) => ({ default: m.HardwarePage })))
const HardwarePricesPage = lazy(() => import(/* webpackChunkName: "tab-hardware" */ './routes/chunks/tab-hardware').then((m) => ({ default: m.HardwarePricesPage })))
const HardwareDetailPage = lazy(() => import(/* webpackChunkName: "tab-hardware" */ './routes/chunks/tab-hardware').then((m) => ({ default: m.HardwareDetailPage })))
const HealthPage = lazy(() => import(/* webpackChunkName: "tab-health" */ './routes/chunks/tab-health').then((m) => ({ default: m.HealthPage })))
const MorePage = lazy(() => import(/* webpackChunkName: "tab-more" */ './routes/chunks/tab-more').then((m) => ({ default: m.MorePage })))

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { configured, loading, session } = useAuth()
  const location = useLocation()
  // 认证状态解析中（getSession 初始化期）：登录页沿用登录卡骨架；其它路由显示
  // 全局启动骨架，避免硬刷新任意页面时白屏数百毫秒到数秒。
  if (loading) {
    return location.pathname === '/login' ? <LoginPageSkeleton /> : <BootSkeleton />
  }
  // 未配置认证时直接放行（本地开发兜底）；配置了但没登录则跳登录页
  if (configured && !session && location.pathname !== '/login') {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function AppFrame() {
  const location = useLocation()
  const navigate = useNavigate()
  const collapsed = useUiStore((state) => state.sidebarCollapsed)
  const dark = useUiStore((state) => state.dark)
  const stats = useStats()

  const routeTab = tabForPath(location.pathname)
  const activeId = routeTab ?? TABS[0].id

  useEffect(() => { applyTheme(dark ? 'dark' : 'light') }, [dark])

  useEffect(() => {
    if (routeTab !== undefined) uiStore.actions.selectTab(routeTab)
  }, [routeTab])

  const pending = stats.data?.inbox.pending_review ?? 0
  const unread = stats.data?.messages_unread ?? 0

  const navItems: NavItem[] = TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    ...(tab.id === 'inbox' && pending > 0 ? { badge: pending } : {}),
    ...(tab.id === 'messages' && unread > 0 ? { badge: unread } : {}),
  }))

  // 登录页独立渲染，不带 AppShell
  if (location.pathname === '/login') {
    return (
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ErrorBoundary>
    )
  }

  return (
    <AppShell
      brand={<span className={css.brandText}>统一控制台</span>}
      items={navItems}
      activeId={activeId}
      onSelect={(id) => { if (isTabId(id)) navigate(pathForTab(id)) }}
      sidebarCollapsed={collapsed}
      onToggleSidebar={() => { uiStore.actions.toggleSidebar() }}
      sidebarFooter={(
        <Button
          variant="ghost"
          size="sm"
          icon={dark ? <IconSun size={16} /> : <IconMoon size={16} />}
          aria-label={dark ? '切换到浅色' : '切换到深色'}
          title={dark ? '切换到浅色' : '切换到深色'}
          onClick={() => { uiStore.actions.toggleDark() }}
        />
      )}
    >
      <ErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<div className={css.routeFallback}><LoadingSkeleton variant="inline" /></div>}>
          <Routes>
            <Route path="/" element={<Navigate to={DEFAULT_PATH} replace />} />
            <Route path="/assets" element={<Navigate to="/assets/overview" replace />} />
            <Route path="/assets/:tab" element={<AssetsPage />} />
            <Route path="/kb" element={<Navigate to="/kb/index" replace />} />
            <Route path="/kb/inbox" element={<InboxTriage />} />
            <Route path="/kb/index" element={<EntriesTab />} />
            <Route path="/kb/favorites" element={<EntriesTab favoritesOnly />} />
            <Route path="/kb/review" element={<DailyReview />} />
            <Route path="/kb/summary" element={<DailySummary />} />
            <Route path="/kb/search" element={<SemanticSearch />} />
            <Route path="/kb/v3" element={<V3ResourcesTab />} />
            <Route path="/inbox" element={<InboxTab />} />
            <Route path="/messages" element={<MessagesTab />} />
            <Route path="/hardware" element={<HardwarePage />} />
            <Route path="/hardware/prices" element={<HardwarePricesPage />} />
            <Route path="/hardware/:projectId" element={<HardwareDetailPage />} />
            <Route path="/health" element={<HealthPage />} />
            <Route path="/more" element={<MorePage />} />
            <Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <AuthProvider>
          <RequireAuth>
            <AppFrame />
          </RequireAuth>
        </AuthProvider>
      </HashRouter>
    </QueryClientProvider>
  )
}
